import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {ApiError} from '@mintdata-wsp/common/controllers/network/Fetcher';
import {AppOAuth2CredentialsCreateRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/AppOAuth2CredentialsCreateRequest';
import {AppOAuth2CredentialsUpdateRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/AppOAuth2CredentialsUpdateRequest';
import {AppOAuth2CredentialsResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/AppOAuth2CredentialsResponse';
import {AppOAuth2ListCredentialsResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/AppOAuth2ListCredentialsResponse';
import {API} from '@mintdata-wsp/common/controllers/network/WorkspaceAPI';
import {Credentials} from '@mintdata-wsp/common/state/DataState';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {ErrorSubMessages} from '@mintdata-wsp/common/state/ui/visualDesigner/ToastData';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import bind from 'bind-decorator';
import {action} from 'mobx';

export class CredentialsController
{
    @bind
    private _currentContext():{organizationSid:string}
    {
        const {context} = commonControllers;

        const organizationSid = context.currentOrganizationSid();

        return {organizationSid};
    }

    public syncAllCredentials()
    {
        const organizationSid = this._currentContext().organizationSid;
        return this.getAllCredentials(organizationSid)
            .then(action((allCredentials:Credentials[]) =>
            {
                rootState.data.credentials = allCredentials;
            }))
            .catch(action(() =>
            {
                rootState.data.credentials = [];
            }));
    }

    public getAllCredentials(organisationSid:string):Promise<Credentials[]>
    {
        return new Promise(action((resolve, reject) =>
        {
            if( rootState.userSession )
            {
                const organizationSid = this._currentContext().organizationSid;

                API.appOauth2Credentials.listAppOauth2CredentialsByOrganization(organizationSid)
                    .then(action((response:AppOAuth2ListCredentialsResponse) =>
                    {
                        if( ApiError.isError(response) )
                        {
                            controllers.ui.toasts.showError(response);
                            reject(null);
                        }
                        else
                        {
                            if( !response.appOAuth2Credentials )
                                throw {
                                    message: 'Ops!',
                                    subMessage: ErrorSubMessages.Support
                                };

                            let credentialsList = response.appOAuth2Credentials.map((credentials:any) =>
                            {
                                return Credentials.fromResponse(credentials);
                            });

                            resolve(credentialsList);
                        }
                    }));
            }
            else
                reject(null);
        }));
    }

    public createCredentials(credentials:Credentials):Promise<Credentials>
    {
        return new Promise(action((resolve, reject) =>
        {
            if( rootState.userSession )
            {
                const {
                    name,
                    provider,
                    clientId,
                    clientSecret,
                    scopes,
                    customDomain,
                    apiKey
                } = credentials;

                const createRequest = new AppOAuth2CredentialsCreateRequest(
                    clientId,
                    clientSecret,
                    scopes,
                    customDomain,
                    apiKey
                );
                const organizationSid = this._currentContext().organizationSid;

                API.appOauth2Credentials.createAppOAuth2Credentials(organizationSid, provider, name, createRequest)
                    .then(action((response:AppOAuth2CredentialsResponse) =>
                    {
                        if( ApiError.isError(response) )
                        {
                            controllers.ui.toasts.showError(response);
                            reject(null);
                        }
                        else
                        {
                            const newCredentials = Credentials.fromResponse(response);
                            rootState.data.credentials.push(newCredentials);

                            resolve(newCredentials);
                        }
                    }));
            }
            else
                reject(null);
        }));
    }

    public updateCredentials(credentials:Credentials, newCredentials:Credentials):Promise<Credentials>
    {
        return new Promise(action((resolve, reject) =>
        {
            if( rootState.userSession )
            {
                const {
                    name,
                    provider,
                    clientId,
                    clientSecret,
                    scopes,
                    customDomain,
                    apiKey
                } = newCredentials;

                const updateRequest = new AppOAuth2CredentialsUpdateRequest(
                    name,
                    provider,
                    clientId,
                    clientSecret,
                    scopes,
                    customDomain,
                    apiKey
                );

                const prevCredentials:Credentials = {...credentials};

                // for fast ui reaction
                credentials.name = name;
                credentials.clientId = clientId;
                credentials.clientSecret = clientSecret;
                credentials.provider = provider;
                credentials.scopes = scopes;
                const organizationSid = this._currentContext().organizationSid;

                API.appOauth2Credentials.updateAppOauth2Credentials(organizationSid, prevCredentials.provider, prevCredentials.name, updateRequest)
                    .then(action((response:AppOAuth2CredentialsResponse) =>
                    {
                        if( ApiError.isError(response) )
                        {
                            credentials.name = prevCredentials.name;
                            credentials.clientId = prevCredentials.clientId;
                            credentials.clientSecret = prevCredentials.clientSecret;
                            credentials.provider = prevCredentials.provider;
                            credentials.scopes = prevCredentials.scopes;

                            controllers.ui.toasts.showError(response);
                            reject(null);
                        }
                        else
                        {
                            resolve();
                        }
                    }));
            }
            else
                reject(null);
        }));
    }

    public deleteCredentials(creds:Credentials):Promise<any>
    {
        return new Promise(action((resolve, reject) =>
        {
            if( rootState.userSession )
            {
                const organizationSid = this._currentContext().organizationSid;

                API.appOauth2Credentials.deleteAppCredentials(organizationSid, creds.provider, creds.name)
                    .then(action((response:Response) =>
                    {
                        if( ApiError.isError(response) )
                        {
                            controllers.ui.toasts.showError(response);
                            reject(null);
                        }
                        else
                        {
                            const {credentials} = rootState.data;
                            const index = credentials.indexOf(creds);

                            credentials.splice(index, 1);

                            resolve();
                        }
                    }));
            }
            else
                reject(null);
        }));
    }
}
